import React, { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { expertDashboardApi } from '../../../lib/http';
import { ScheduleException } from '../../../types/working-hours';
import TimePicker from '../../ui/TimePicker';

interface ExceptionFormPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editException: ScheduleException | null;
}

const ExceptionFormPanel: React.FC<ExceptionFormPanelProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editException,
}) => {
  const dateInputRef = useRef<HTMLInputElement>(null);
  const endDateInputRef = useRef<HTMLInputElement>(null);
  const [date, setDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showEndDate, setShowEndDate] = useState(false);
  const [isClosed, setIsClosed] = useState(true);
  const [startMin, setStartMin] = useState(540);
  const [endMin, setEndMin] = useState(1020);
  const [reason, setReason] = useState('');
  const [recurringYearly, setRecurringYearly] = useState(false);

  const isRange = Boolean(endDate) && endDate !== date;
  const endDateInvalid = Boolean(endDate) && endDate < date;

  // Initialize form
  useEffect(() => {
    if (isOpen) {
      if (editException) {
        setDate(editException.date);
        setEndDate(editException.end_date ?? '');
        setShowEndDate(Boolean(editException.end_date));
        setIsClosed(editException.is_closed);
        setStartMin(editException.start_min ?? 540);
        setEndMin(editException.end_min ?? 1020);
        setReason(editException.reason || '');
        setRecurringYearly(editException.recurring_yearly);
      } else {
        // Default to tomorrow
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setDate(tomorrow.toISOString().split('T')[0]);
        setEndDate('');
        setShowEndDate(false);
        setIsClosed(true);
        setStartMin(540);
        setEndMin(1020);
        setReason('');
        setRecurringYearly(false);
      }
    }
  }, [isOpen, editException]);

  // When a range is selected, recurring yearly is not supported
  useEffect(() => {
    if (isRange && recurringYearly) {
      setRecurringYearly(false);
    }
  }, [isRange, recurringYearly]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const data = {
        date,
        end_date: endDate && endDate !== date ? endDate : undefined,
        is_closed: isClosed,
        start_min: isClosed ? undefined : startMin,
        end_min: isClosed ? undefined : endMin,
        reason: reason || undefined,
        recurring_yearly: recurringYearly,
      };

      if (editException) {
        return expertDashboardApi.scheduleExceptions.update(editException.id, data);
      } else {
        return expertDashboardApi.scheduleExceptions.create(data);
      }
    },
    onSuccess: () => {
      toast.success(editException ? 'Exception updated' : 'Exception created');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to save exception');
    },
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              {editException ? 'Edit Exception' : 'Add Exception'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <div className="space-y-5">
            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {showEndDate ? 'Start Date' : 'Date'}
              </label>
              <input
                ref={dateInputRef}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                onClick={() => dateInputRef.current?.showPicker?.()}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 cursor-pointer"
              />
            </div>

            {/* End Date (optional, for ranges) */}
            {showEndDate ? (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    End Date
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEndDate(false);
                      setEndDate('');
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Remove
                  </button>
                </div>
                <input
                  ref={endDateInputRef}
                  type="date"
                  value={endDate}
                  min={date}
                  onChange={(e) => setEndDate(e.target.value)}
                  onClick={() => endDateInputRef.current?.showPicker?.()}
                  className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 cursor-pointer ${
                    endDateInvalid
                      ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-purple-500 focus:border-purple-500'
                  }`}
                />
                {endDateInvalid && (
                  <p className="mt-1 text-xs text-red-600">
                    End date must be on or after the start date.
                  </p>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowEndDate(true)}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium"
              >
                + Add end date (range)
              </button>
            )}

            {/* Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Exception Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setIsClosed(true)}
                  className={`
                    p-3.5 rounded-xl border-2 text-left transition-all
                    ${isClosed
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      isClosed ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <div>
                      <div className={`text-sm font-medium ${isClosed ? 'text-red-900' : 'text-gray-700'}`}>
                        Closed
                      </div>
                      <div className={`text-xs ${isClosed ? 'text-red-600' : 'text-gray-500'}`}>
                        Not available
                      </div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setIsClosed(false)}
                  className={`
                    p-3.5 rounded-xl border-2 text-left transition-all
                    ${!isClosed
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      !isClosed ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <div className={`text-sm font-medium ${!isClosed ? 'text-amber-900' : 'text-gray-700'}`}>
                        Modified
                      </div>
                      <div className={`text-xs ${!isClosed ? 'text-amber-600' : 'text-gray-500'}`}>
                        Different hours
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Time Range (only if modified hours) */}
            {!isClosed && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Working Hours
                </label>
                <div className="flex items-center gap-3">
                  <TimePicker
                    value={startMin}
                    onChange={setStartMin}
                    max={1425}
                    aria-label="Start time"
                    className="flex-1 flex items-center justify-between gap-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <span className="text-gray-400 text-sm">to</span>
                  <TimePicker
                    value={endMin}
                    onChange={setEndMin}
                    min={startMin + 15}
                    aria-label="End time"
                    className="flex-1 flex items-center justify-between gap-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Vacation, Doctor's appointment"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            {/* Recurring */}
            <div className="flex items-start gap-3">
              <button
                type="button"
                disabled={isRange}
                onClick={() => setRecurringYearly(!recurringYearly)}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 rounded-full
                  border-2 border-transparent transition-colors duration-200
                  ${recurringYearly && !isRange ? 'bg-purple-600' : 'bg-gray-200'}
                  ${isRange ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                `}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full
                    bg-white shadow transition duration-200
                    ${recurringYearly && !isRange ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
              <div>
                <div className={`text-sm font-medium ${isRange ? 'text-gray-400' : 'text-gray-900'}`}>
                  Repeat yearly
                </div>
                <div className="text-xs text-gray-500">
                  {isRange
                    ? "Repeating ranges aren't supported yet."
                    : 'This exception will apply on the same date every year'}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={!date || endDateInvalid || saveMutation.isPending}
              className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {saveMutation.isPending ? 'Saving...' : editException ? 'Update' : 'Add Exception'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExceptionFormPanel;
